import { cosA, idiv, imax, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, LashBarrel } from '../types.ts';
import { damageEnemy, ev, isFlying } from './combat.ts';

/**
 * The lash (El Cuerno 2, docs/SAGAS.md): the titan swings its tail in its sleep, with the clan's summer camp on its
 * tip. Every so often, after a warning, the ground tilts to one side and everything loose slides: heroes (unless they
 * hold on by a mooring post), nightmares (the ones near the edge go over it, into the sea) and the clan's barrels,
 * which roll on and knock down what they meet. The last tilt is the big one. The camp is held as any node.
 */

export const lashTick = (s: BattleState): void => {
  const r = s.rules?.lash;
  if (!r) return;
  const st = (s.lash ??= { n: 0, next: r.first, warn: 0, slide: 0, dx: 0, dy: 0, fx: 0, fy: 0, barrels: r.barrels.map((b, i) => ({ id: i + 1, x: b.x, y: b.y, roll: 0, dx: 0, dy: 0, hit: [] })), fell: 0 });
  if (!s.started) return;
  // The warning: a creak, dust, the camera leaning (the renderer); then it goes.
  if (st.warn === 0 && st.slide === 0 && st.n < r.total && s.waveT >= st.next) {
    st.warn = r.warn;
    ev(s, { t: 'lash', kind: 'warn', x: 0, y: 0, a: axisOf(r.axis, st.n) });
  }
  if (st.warn > 0 && --st.warn === 0) slide(s, st);
  if (st.slide > 0) {
    st.slide--;
    for (const e of s.enemies) {
      if (e.hp <= 0 || isFlying(e)) continue;
      e.x += st.fx;
      e.y += st.fy;
      // Over the edge: into the sea.
      if (!withinRange(e.x, e.y, 0, 0, r.edge)) {
        e.hp = 0;
        st.fell++;
        ev(s, { t: 'lash', kind: 'fall', x: e.x, y: e.y });
      }
    }
    if (st.slide === 0) st.next += r.every;
  }
  const keep: LashBarrel[] = [];
  for (const b of st.barrels) {
    if (b.roll > 0) {
      b.roll--;
      b.x += b.dx;
      b.y += b.dy;
      for (const e of s.enemies) {
        if (e.hp <= 0 || isFlying(e) || b.hit.includes(e.id) || !withinRange(e.x, e.y, b.x, b.y, r.barrel.r)) continue;
        b.hit.push(e.id);
        e.stun = imax(e.stun, r.barrel.stun);
        damageEnemy(s, e, r.barrel.damage, { pid: '', ignoresArmor: true, noReact: true });
        ev(s, { t: 'lash', kind: 'crush', x: e.x, y: e.y });
      }
      if (!withinRange(b.x, b.y, 0, 0, r.edge)) {
        ev(s, { t: 'lash', kind: 'fall', x: b.x, y: b.y });
        continue;
      }
    }
    keep.push(b);
  }
  st.barrels = keep;
};

/** The way the n-th tilt goes: one side, then the other. */
const axisOf = (axis: number, n: number): number => axis + (n % 2) * 512;

/** It goes: everything loose starts sliding the same way; the heroes by a post hold on. */
const slide = (s: BattleState, st: NonNullable<BattleState['lash']>): void => {
  const r = s.rules?.lash;
  if (!r) return;
  const big = st.n === r.total - 1;
  const a = axisOf(r.axis, st.n);
  st.n++;
  const far = big ? mulBp(r.slide, r.bigBp) : r.slide;
  st.dx = idiv(idiv(cosA(a) * far, 10000), r.ticks);
  st.dy = idiv(idiv(sinA(a) * far, 10000), r.ticks);
  st.fx = mulBp(st.dx, r.foeBp);
  st.fy = mulBp(st.dy, r.foeBp);
  st.slide = r.ticks;
  ev(s, { t: 'lash', kind: big ? 'big' : 'slide', x: 0, y: 0, a });
  for (const h of s.heroes) {
    if (h.downTicks > 0 || r.posts.some((p) => withinRange(p.x, p.y, h.x, h.y, r.postR))) continue;
    h.shove = r.ticks;
    h.shoveX = st.dx;
    h.shoveY = st.dy;
  }
  // The barrels roll farther than anything, and faster.
  const d = isqrt(st.dx * st.dx + st.dy * st.dy);
  for (const b of st.barrels) {
    b.roll = r.barrel.ticks;
    b.dx = d ? idiv(st.dx * r.barrel.speed, d) : 0;
    b.dy = d ? idiv(st.dy * r.barrel.speed, d) : 0;
    b.hit = [];
    ev(s, { t: 'lash', kind: 'barrel', x: b.x, y: b.y });
  }
};
